/*
 * DO NOT DELETE — base-info and page-design tags are consumed by project-snapshot tooling for quick page overview. Always update them to reflect actual page content.
 * <base-info>
 * Description: Form to create a new component entry with initial assets and docs, with live preview panel
 * Style referenceFiles:
 * Design for: Desktop
 * </base-info>
 * <page-design>
 * ## Features & Interactions
 * - Left panel: Input basic info (name, category, description), code snippet, props, guidelines
 * - Right panel: Live component preview with theme toggle, variant/size/state controls, checkered background
 * - Submit to create
 * - Navigate to: ComponentList (Cancel), ComponentDetail (Success)
 * </page-design>
 */

import { useState } from 'react'
import { useAtomValue, useSetAtom } from 'jotai'
import { Link, useNavigate } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { LayoutSidebar } from '@/components/function/LayoutSidebar'
import * as ComponentNewStore from '@/stores/ComponentNewStore'
import { pageLinks } from '@/pageLinks'

const inputCls = "w-full px-4 py-3 bg-white rounded-xl text-sm outline-none shadow-[0_2px_10px_rgb(0,0,0,0.02)] border border-transparent focus:border-[#1D263A]/10 focus:shadow-[0_4px_12px_rgb(29,38,58,0.08)] transition-all placeholder-gray-400"

function FormFields() {
  const form = useAtomValue(ComponentNewStore.formStateAtom)
  const updateForm = useSetAtom(ComponentNewStore.updateFormAtom)
  const { data } = useAtomValue(ComponentNewStore.refreshAtom)

  return (
    <div className="flex flex-col gap-8">
      <section>
        <h2 className="text-base font-bold mb-4">基本信息</h2>
        <div className="flex flex-col gap-4">
          <div>
            <label className="text-sm font-medium text-gray-600 mb-1.5 block">组件名称 *</label>
            <input type="text" value={form.name} onChange={e => updateForm({ name: e.target.value })} placeholder="例如：Button" className={inputCls} required maxLength={200} />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600 mb-1.5 block">分类</label>
            <select value={form.categoryId} onChange={e => updateForm({ categoryId: e.target.value })} className={inputCls}>
              <option value="">请选择分类</option>
              {data?.categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600 mb-1.5 block">描述</label>
            <textarea value={form.description} onChange={e => updateForm({ description: e.target.value })} placeholder="组件的用途和使用场景..." rows={3} className={inputCls + ' resize-none'} />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600 mb-1.5 block">标签</label>
            <input type="text" value={form.tags} onChange={e => updateForm({ tags: e.target.value })} placeholder="用逗号分隔，例如：基础,表单,交互" className={inputCls} />
          </div>
        </div>
      </section>
      <section>
        <h2 className="text-base font-bold mb-4">代码片段</h2>
        <textarea value={form.codeSnippet} onChange={e => updateForm({ codeSnippet: e.target.value })} placeholder="粘贴组件代码示例..." rows={8} className={inputCls + ' resize-none font-mono text-xs'} />
      </section>
      <section>
        <h2 className="text-base font-bold mb-4">Props / API 文档</h2>
        <textarea value={form.propsDoc} onChange={e => updateForm({ propsDoc: e.target.value })} placeholder="Markdown 格式的属性说明表格..." rows={6} className={inputCls + ' resize-none font-mono text-xs'} />
      </section>
      <section>
        <h2 className="text-base font-bold mb-4">使用指南</h2>
        <textarea value={form.guidelines} onChange={e => updateForm({ guidelines: e.target.value })} placeholder="组件的使用规范、Do/Don't..." rows={6} className={inputCls + ' resize-none'} />
      </section>
    </div>
  )
}

/* ─── 实时预览面板 ─── */
function PreviewPanel() {
  const form = useAtomValue(ComponentNewStore.formStateAtom)
  const [theme, setTheme] = useState<'light' | 'dark'>('light')
  const [variant, setVariant] = useState('primary')
  const [size, setSize] = useState('md')
  const [state, setState] = useState('默认')
  const [radius, setRadius] = useState('12')

  const displayName = form.name.trim() || '组件名称'
  const isDark = theme === 'dark'
  const states = ['默认', '悬停', '聚焦', '激活', '禁用']

  const variantStyles: Record<string, string> = {
    primary: isDark
      ? 'bg-[#1D263A] text-white shadow-[0_4px_12px_rgba(29,38,58,0.4)]'
      : 'bg-[#1D263A] text-white shadow-[0_4px_12px_rgba(29,38,58,0.25)]',
    secondary: isDark
      ? 'bg-gray-700 text-gray-200'
      : 'bg-gray-100 text-gray-700',
    outline: isDark
      ? 'bg-transparent border-2 border-gray-500 text-gray-200'
      : 'bg-transparent border-2 border-gray-200 text-gray-700',
    ghost: isDark
      ? 'bg-transparent text-gray-300 hover:bg-gray-800'
      : 'bg-transparent text-gray-600 hover:bg-gray-50',
    danger: 'bg-red-600 text-white shadow-[0_4px_12px_rgba(239,68,68,0.25)]',
  }

  const sizeStyles: Record<string, string> = {
    xs: 'text-xs px-3 py-1.5',
    sm: 'text-sm px-4 py-2',
    md: 'text-sm px-5 py-2.5',
    lg: 'text-base px-6 py-3',
    xl: 'text-lg px-8 py-3.5',
  }

  const stateStyles: Record<string, string> = {
    '默认': '',
    '悬停': 'scale-105 brightness-110',
    '聚焦': 'ring-2 ring-[#1D263A]/30 ring-offset-2',
    '激活': 'scale-95',
    '禁用': 'opacity-50 cursor-not-allowed',
  }

  return (
    <div className="flex flex-col h-full">
      {/* 预览区头部 */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <Icon icon="lucide:eye" className="text-lg text-gray-400" />
          <span className="text-sm font-bold text-gray-900">实时预览</span>
        </div>
        <div className="flex items-center gap-1.5 bg-gray-50 rounded-lg p-1">
          <button onClick={() => setTheme('light')} className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all ${!isDark ? 'bg-white text-[#1D263A] shadow-sm' : 'text-gray-500'}`}>
            <Icon icon="lucide:sun" className="text-sm" />
          </button>
          <button onClick={() => setTheme('dark')} className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all ${isDark ? 'bg-gray-700 text-white shadow-sm' : 'text-gray-500'}`}>
            <Icon icon="lucide:moon" className="text-sm" />
          </button>
        </div>
      </div>

      {/* 主预览画布 */}
      <div className={`flex-1 flex items-center justify-center p-6 transition-colors duration-300 ${isDark ? 'bg-gray-900' : "bg-[image:linear-gradient(45deg,#f0f0f0_25%,transparent_25%,transparent_75%,#f0f0f0_75%,#f0f0f0),linear-gradient(45deg,#f0f0f0_25%,transparent_25%,transparent_75%,#f0f0f0_75%,#f0f0f0)] bg-[size:16px_16px] bg-[position:0_0,8px_8px]"}`}>
        <div className="flex flex-col items-center gap-6">
          {/* 组件名标签 */}
          <div className={`text-xs font-mono px-2.5 py-1 rounded-md ${isDark ? 'bg-gray-800 text-gray-400' : 'bg-white/80 text-gray-500 shadow-sm'}`}>
            &lt;{displayName} /&gt;
          </div>

          {/* 渲染的组件 */}
          <div className="flex flex-col items-center gap-4">
            {/* 主按钮预览 */}
            <button
              className={`font-medium transition-all duration-200 ${variantStyles[variant] || variantStyles.primary} ${sizeStyles[size] || sizeStyles.md} ${stateStyles[state] || ''}`}
              style={{ borderRadius: `${radius}px` }}
              disabled={state === '禁用'}
            >
              {state === '禁用' ? '不可用' : displayName}
            </button>

            {/* 变体一览 - 小尺寸展示所有变体 */}
            <div className="flex items-center gap-2 mt-2">
              {Object.keys(variantStyles).map(v => (
                <button
                  key={v}
                  className={`px-3 py-1.5 text-xs font-medium transition-all duration-200 ${variantStyles[v]} ${v === variant ? 'ring-2 ring-[#1D263A]/20 ring-offset-2' : 'opacity-60'}`}
                  style={{ borderRadius: `${radius}px` }}
                  onClick={() => setVariant(v)}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>

          {/* 尺寸对比展示 */}
          <div className={`flex items-end gap-3 px-4 py-3 rounded-xl ${isDark ? 'bg-gray-800/50' : 'bg-white/60'}`}>
            {Object.entries(sizeStyles).map(([s, cls]) => (
              <button
                key={s}
                className={`font-medium transition-all duration-200 ${variantStyles[variant]} ${cls} ${s === size ? '' : 'opacity-40'}`}
                style={{ borderRadius: `${radius}px` }}
                onClick={() => setSize(s)}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* 控制区 */}
      <div className="border-t border-gray-100 px-5 py-4 space-y-4 bg-white">
        {/* 交互状态 */}
        <div>
          <label className="text-xs font-semibold text-gray-400 mb-2 block">交互状态</label>
          <div className="flex gap-1.5">
            {states.map(s => (
              <button key={s} onClick={() => setState(s)} className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all ${state === s ? 'bg-[#1D263A]/5 text-[#1D263A]' : 'text-gray-500 hover:bg-gray-50'}`}>{s}</button>
            ))}
          </div>
        </div>

        {/* 圆角调节 */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-semibold text-gray-400">圆角</label>
            <span className="text-xs font-mono text-gray-500">{radius}px</span>
          </div>
          <input type="range" min="0" max="24" value={radius} onChange={e => setRadius(e.target.value)} className="w-full h-1.5 bg-gray-100 rounded-full appearance-none cursor-pointer accent-[#1D263A]" />
        </div>
      </div>
    </div>
  )
}

export default function ComponentNew() {
  const navigate = useNavigate()
  const form = useAtomValue(ComponentNewStore.formStateAtom)
  const submit = useSetAtom(ComponentNewStore.submitAtom)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const created = await submit()
    if (created && 'id' in created) void navigate(pageLinks.ComponentDetail(created.id))
  }

  return (
    <div className="flex h-screen w-full bg-[#F8F9FA] font-['Noto_Sans_SC',sans-serif] text-[#1A1D25] overflow-hidden">
      <LayoutSidebar />
      <div className="flex-1 flex flex-col h-full overflow-hidden">
        {/* 顶部导航 */}
        <header className="flex items-center justify-between px-8 py-4 bg-[#F8F9FA]">
          <div className="flex items-center gap-3">
            <Link to={pageLinks.ComponentList()} className="w-10 h-10 rounded-xl bg-white flex items-center justify-center text-gray-600 shadow-[0_2px_10px_rgb(0,0,0,0.02)] hover:shadow-[0_4px_12px_rgb(0,0,0,0.06)] transition-all"><Icon icon="lucide:arrow-left" className="text-xl" /></Link>
            <h1 className="text-2xl font-bold tracking-tight">新建组件</h1>
          </div>
          <div className="flex items-center gap-3">
            <Link to={pageLinks.ComponentList()} className="px-5 py-2.5 rounded-xl text-sm font-medium text-gray-600 bg-white shadow-[0_2px_10px_rgb(0,0,0,0.02)] hover:shadow-[0_4px_12px_rgb(0,0,0,0.06)] transition-all">取消</Link>
            <button onClick={handleSubmit} disabled={form.isSubmitting} className="bg-[#1D263A] hover:bg-[#151D2E] disabled:opacity-50 text-white font-medium py-2.5 px-5 rounded-xl shadow-[0_4px_12px_rgba(29,38,58,0.25)] transition-all text-sm">{form.isSubmitting ? '创建中...' : '创建组件'}</button>
          </div>
        </header>

        {/* 左右分栏 */}
        <div className="flex-1 flex overflow-hidden px-6 pb-6 gap-6">
          {/* 左侧 - 表单 */}
          <form onSubmit={handleSubmit} className="w-[55%] overflow-y-auto pr-2 custom-scrollbar">
            <div className="bg-white rounded-[24px] shadow-[0_8px_30px_rgb(0,0,0,0.03)] p-8">
              <FormFields />
            </div>
          </form>

          {/* 右侧 - 预览 */}
          <div className="w-[45%] bg-white rounded-[24px] shadow-[0_8px_30px_rgb(0,0,0,0.03)] overflow-hidden flex flex-col">
            <PreviewPanel />
          </div>
        </div>
      </div>
    </div>
  )
}
